import { useEffect, useRef, useState } from "react";
import { X, Wind, Zap, RotateCcw } from "lucide-react";
import { IDS } from "../constants/testIds";

/**
 * Hidden debug drawer for previewing any wind strength / triggering wakes
 * without waiting on real seismic data.
 *
 * Opens with the `W` key or the tiny "wind" stamp in the footer.
 * When "override" is active the parent uses this component's wind value
 * instead of the auto-computed one.
 */
export default function WindDebug({
  open,
  onClose,
  override,
  setOverride,
  onTriggerWake,
  autoWind,
}) {
  const [wind, setWind] = useState(override.value);
  const [magPreview, setMagPreview] = useState(5.5);
  const panelRef = useRef(null);

  // Keep local slider in sync when override toggles or auto wind changes.
  useEffect(() => {
    setWind(override.value);
  }, [override.value]);

  const applyOverride = (v, active = true) => {
    setWind(v);
    setOverride({ active, value: v });
  };

  const disableOverride = () => setOverride({ active: false, value: autoWind });

  if (!open) return null;

  const pct = Math.round(wind * 100);
  const label =
    pct < 15
      ? "Deep sleep"
      : pct < 40
        ? "Gentle breeze"
        : pct < 70
          ? "Restless coat"
          : pct < 100
            ? "Storm"
            : "Hurricane";

  return (
    <div
      data-testid={IDS.windDebug}
      role="dialog"
      aria-label="Wind debug panel"
      className="fixed left-1/2 -translate-x-1/2 bottom-4 z-50 w-[92vw] max-w-[720px] bg-[hsl(var(--paper))] border border-[hsl(var(--ink))]/60 shadow-[6px_8px_0_hsl(24_22%_10%/0.12)] px-5 py-4"
      ref={panelRef}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-baseline gap-3">
          <div className="mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--ink-2))]">
            Workshop drawer
          </div>
          <div className="font-display italic text-lg leading-none">wind &amp; wake</div>
          <span className="mono text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5 border border-[hsl(var(--seismic))] text-[hsl(var(--seismic))]">
            hidden
          </span>
        </div>
        <button
          onClick={onClose}
          aria-label="Close debug panel"
          data-testid={IDS.windDebugClose}
          className="rounded-full border border-[hsl(var(--ink))]/50 h-8 w-8 flex items-center justify-center hover:bg-[hsl(var(--ink))]/5 transition-colors"
        >
          <X size={14} />
        </button>
      </div>

      {/* Wind slider */}
      <div className="mb-4">
        <div className="flex items-baseline justify-between mb-1">
          <div className="flex items-center gap-2 mono text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--ink-2))]">
            <Wind size={12} /> Wind
            {!override.active && (
              <span className="text-[hsl(var(--ink-mid))] normal-case tracking-normal">
                (auto {Math.round(autoWind * 100)}%)
              </span>
            )}
          </div>
          <div className="flex items-baseline gap-3">
            <span className="font-display text-2xl leading-none">{pct}%</span>
            <span
              className="mono text-[10px] uppercase tracking-[0.22em]"
              style={{ color: pct >= 100 ? "hsl(var(--seismic))" : "hsl(var(--ink-2))" }}
            >
              {label}
            </span>
          </div>
        </div>
        <input
          type="range"
          min={0}
          max={1.4}
          step={0.01}
          value={wind}
          data-testid={IDS.windSlider}
          onChange={(e) => applyOverride(parseFloat(e.target.value))}
          className="wind-range w-full"
        />
        <div className="flex justify-between mono text-[9px] text-[hsl(var(--ink-mid))] mt-1 tracking-widest">
          <span>0</span>
          <span>25</span>
          <span>50</span>
          <span>75</span>
          <span>100</span>
          <span>140</span>
        </div>
      </div>

      {/* Presets */}
      <div className="flex flex-wrap gap-2 mb-4">
        {[
          { label: "Calm", v: 0.06 },
          { label: "Breeze", v: 0.25 },
          { label: "Gust", v: 0.55 },
          { label: "Storm", v: 0.9 },
          { label: "Hurricane", v: 1.3 },
        ].map((p) => (
          <button
            key={p.label}
            data-testid={`wind-preset-${p.label.toLowerCase()}`}
            onClick={() => applyOverride(p.v)}
            className={`mono text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 border transition-colors ${
              Math.abs(wind - p.v) < 0.02
                ? "bg-[hsl(var(--ink))] text-[hsl(var(--paper))] border-[hsl(var(--ink))]"
                : "border-[hsl(var(--ink))]/50 hover:bg-[hsl(var(--ink))]/5"
            }`}
          >
            {p.label} · {Math.round(p.v * 100)}%
          </button>
        ))}
      </div>

      {/* Wake trigger + magnitude preview */}
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-4 items-end pt-3 border-t border-[hsl(var(--ink))]/30">
        <div>
          <div className="flex items-baseline justify-between mb-1">
            <span className="mono text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--ink-2))]">
              Trigger magnitude
            </span>
            <span className="font-display text-xl leading-none">M {magPreview.toFixed(1)}</span>
          </div>
          <input
            type="range"
            min={2}
            max={8}
            step={0.1}
            value={magPreview}
            data-testid={IDS.magSlider}
            onChange={(e) => setMagPreview(parseFloat(e.target.value))}
            className="wind-range w-full"
          />
          <div className="flex justify-between mono text-[9px] text-[hsl(var(--ink-mid))] mt-1 tracking-widest">
            <span>M 2</span>
            <span>M 4</span>
            <span className="text-[hsl(var(--seismic))]">M 6</span>
            <span>M 8</span>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            data-testid={IDS.fireBtn}
            onClick={() => onTriggerWake(magPreview)}
            className="mono text-[10px] uppercase tracking-[0.22em] px-3 py-2.5 border border-[hsl(var(--seismic))] bg-[hsl(var(--seismic))] text-[hsl(var(--paper))] hover:brightness-110 transition-all flex items-center gap-2"
          >
            <Zap size={13} /> Fire
          </button>
          <button
            data-testid={IDS.resetBtn}
            onClick={disableOverride}
            className="mono text-[10px] uppercase tracking-[0.22em] px-3 py-2.5 border border-[hsl(var(--ink))]/50 hover:bg-[hsl(var(--ink))]/5 transition-colors flex items-center gap-2"
          >
            <RotateCcw size={13} /> Auto
          </button>
        </div>
      </div>

      <p className="mono text-[9px] text-[hsl(var(--ink-mid))] mt-3 leading-relaxed">
        Press <kbd className="px-1 py-[1px] border border-[hsl(var(--ink))]/40">W</kbd> to toggle this drawer ·
        Press <kbd className="px-1 py-[1px] border border-[hsl(var(--ink))]/40">Esc</kbd> to close ·
        Override mode replaces the auto-computed wind until you tap <em>Auto</em>.
      </p>
    </div>
  );
}
